import React, { useState } from 'react';
import { Alert, App, Button, Form, InputNumber, Modal, Result, Switch, Table, Tag } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { ApiError } from '../../api/types';
import { useUpdateVIPConfig, useVIPConfigs } from './queries';
import { VIPConfig, VIPConfigInput } from './types';

const errorText = (error: unknown) => error instanceof ApiError
  ? `${error.code ? `[${error.code}] ` : ''}${error.message}`
  : error instanceof Error ? error.message : 'Request failed';

function EditModal({ config, onClose }: { config: VIPConfig; onClose: () => void }) {
  const [form] = Form.useForm<VIPConfigInput>();
  const [error, setError] = useState('');
  const save = useUpdateVIPConfig();
  const { message } = App.useApp();
  const submit = async () => {
    try {
      const values = await form.validateFields();
      await save.mutateAsync({ level: config.vip_level, input: { ...values, item_id: values.item_id || null } });
      message.success(`VIP ${config.vip_level} updated`);
      onClose();
    } catch (err) {
      if (err && typeof err === 'object' && 'errorFields' in err) return;
      setError(errorText(err));
    }
  };
  const positive = [{ required: true, type: 'integer' as const, min: 1 }];
  const nonNegative = [{ required: true, type: 'integer' as const, min: 0 }];
  return <Modal title={`Edit VIP ${config.vip_level}`} open onOk={submit} onCancel={onClose} confirmLoading={save.isPending}>
    {error && <Alert type="error" showIcon message={error} className="mb-4" />}
    <Form form={form} layout="vertical" initialValues={config} disabled={save.isPending}>
      <Form.Item name="points_required" label="VIP points required" rules={positive}><InputNumber min={1} precision={0} className="w-full" /></Form.Item>
      <Form.Item name="daily_support_gold" label="Daily support gold (benefit)" rules={positive}><InputNumber min={1} precision={0} className="w-full" /></Form.Item>
      <Form.Item name="gold_award" label="One-time gold reward" rules={nonNegative}><InputNumber min={0} precision={0} className="w-full" /></Form.Item>
      <Form.Item name="gem_award" label="One-time gem reward" rules={nonNegative}><InputNumber min={0} precision={0} className="w-full" /></Form.Item>
      <Form.Item name="item_id" label="Cosmetic inventory item ID" extra="Leave empty to remove the cosmetic reward."><InputNumber min={1} precision={0} className="w-full" /></Form.Item>
      <Form.Item name="tag_up" label="Show upgrade tag" valuePropName="checked"><Switch /></Form.Item>
    </Form>
  </Modal>;
}

export const VIPConfigsPage: React.FC = () => {
  const configs = useVIPConfigs();
  const [editing, setEditing] = useState<VIPConfig | null>(null);
  if (configs.error) return <Result status="error" title="Failed to load VIP configuration" subTitle={errorText(configs.error)} extra={<Button onClick={() => configs.refetch()}>Retry</Button>} />;
  const number = (value: number) => value.toLocaleString();
  const columns: ColumnsType<VIPConfig> = [
    { title: 'VIP', dataIndex: 'vip_level', width: 70 },
    { title: 'Points', dataIndex: 'points_required', render: number },
    { title: 'Daily support', dataIndex: 'daily_support_gold', render: number },
    { title: 'Gold reward', dataIndex: 'gold_award', render: number },
    { title: 'Gem reward', dataIndex: 'gem_award', render: number },
    { title: 'Cosmetic reward', render: (_, row) => row.item_id ? `${row.item_display_name} (${row.item_asset_id}, #${row.item_id})` : '—' },
    { title: 'Tag', dataIndex: 'tag_up', render: (value: boolean) => value ? <Tag color="blue">UPGRADE</Tag> : '—' },
    { title: 'Actions', width: 90, render: (_, row) => <Button type="link" size="small" onClick={() => setEditing(row)}>Edit</Button> },
  ];
  return <div>
    <div className="flex justify-end mb-4"><Button icon={<ReloadOutlined />} loading={configs.isFetching} onClick={() => configs.refetch()}>Refresh</Button></div>
    <Table rowKey="vip_level" columns={columns} dataSource={configs.data || []} loading={configs.isLoading} pagination={false} scroll={{ x: 1000 }} />
    {editing && <EditModal config={editing} onClose={() => setEditing(null)} />}
  </div>;
};
